import React, { useEffect, useState } from "react";
import { Alert, Button, Col, DatePicker, Form, Input, InputNumber, Modal, Row, Select, Typography } from "antd";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";

import { recordInvoicePayment } from "../../../api/finance/FinanceApi";
import { financeKeys } from "../../../api/finance/financeQueryKeys";
import {
	Invoice,
	MANUAL_PAYMENT_METHODS,
	PAYMENT_METHOD_LABELS,
	PAYMENT_WARNING_STRIPE_SETTLEMENT_PENDING,
	PaymentMethod,
} from "../../../types/finance/FinanceTypes";
import { centsToDollarsNumber, formatCents } from "../../../utils/finance/money";
import { financeErrorMessage } from "../../../utils/finance/financeError";
import { useNotifications } from "../../../context/NotificationContext";
import { RecordPaymentFormValues, buildRecordPaymentRequest } from "./recordPaymentRequest";

const { Text } = Typography;

/**
 * Record money that arrived out of band — cash, EFT, cheque.
 *
 * ── Part payments are supported, and one case is refused ─────────────────────────────────────
 *
 * Any amount up to the balance is accepted for a locally-collected invoice. A **Stripe-collected**
 * one may only be settled in FULL out of band, because Stripe's `paid_out_of_band` marks the whole
 * invoice paid and there is no partial equivalent. The server answers that with a 400 whose
 * message says exactly that, and this modal surfaces it verbatim rather than paraphrasing.
 *
 * ── The response replaces local state ────────────────────────────────────────────────────────
 *
 * `amountPaidCents` is RECONCILED server-side from the ledger (never incremented), so the invoice
 * returned by this call is the only correct one. Both the invoice and its ledger are invalidated
 * on success.
 */

export interface RecordPaymentModalProps {
	open: boolean;
	onClose: () => void;
	invoice: Invoice | null;
}

const METHOD_OPTIONS = MANUAL_PAYMENT_METHODS.map((method) => ({
	value: method,
	label: PAYMENT_METHOD_LABELS[method],
}));

const RecordPaymentModal: React.FC<RecordPaymentModalProps> = ({ open, onClose, invoice }) => {
	const [form] = Form.useForm<RecordPaymentFormValues>();
	const queryClient = useQueryClient();
	const { showError, showSuccess, showWarning } = useNotifications();
	const [submitError, setSubmitError] = useState<string | null>(null);

	const balanceDueCents = Number(invoice?.balanceDueCents ?? 0);

	useEffect(() => {
		if (open) {
			setSubmitError(null);
			form.setFieldsValue({
				// Defaulting to the full balance is the common case and makes the Stripe
				// full-settlement rule satisfied by default rather than by instruction.
				amountDollars: centsToDollarsNumber(balanceDueCents),
				method: PaymentMethod.CASH,
				reference: undefined,
				receivedAt: dayjs(),
			});
		}
	}, [open, balanceDueCents, form]);

	const mutation = useMutation({
		mutationFn: (values: RecordPaymentFormValues) =>
			recordInvoicePayment(invoice!.id, buildRecordPaymentRequest(values)),
		onSuccess: (result) => {
			queryClient.invalidateQueries({ queryKey: financeKeys.invoices() });
			queryClient.invalidateQueries({ queryKey: financeKeys.invoice(invoice!.id) });
			queryClient.invalidateQueries({ queryKey: financeKeys.invoicePayments(invoice!.id) });

			if (result.warnings.includes(PAYMENT_WARNING_STRIPE_SETTLEMENT_PENDING)) {
				// The money IS recorded and committed. What failed is telling Stripe, which will
				// otherwise keep dunning the patient — so this is a warning about the OTHER
				// system, not a failed payment.
				showWarning(
					"Payment recorded — Stripe not updated",
					"Stripe still shows this invoice as owing and may keep chasing the patient. Mark it paid in the Stripe dashboard.",
				);
			} else {
				showSuccess("Payment recorded");
			}

			onClose();
		},
		onError: (error) => {
			const message = financeErrorMessage(error, "Failed to record payment");
			setSubmitError(message);
			showError(message);
		},
	});

	return (
		<Modal
			title="Record payment"
			open={open}
			onCancel={onClose}
			width={520}
			destroyOnClose
			footer={[
				<Button key="cancel" onClick={onClose}>
					Cancel
				</Button>,
				<Button
					key="submit"
					type="primary"
					loading={mutation.isPending}
					onClick={() => form.submit()}
					data-testid="record-payment-submit"
				>
					Record payment
				</Button>,
			]}
		>
			{submitError && (
				<Alert
					type="error"
					showIcon
					closable
					style={{ marginBottom: 12 }}
					message={submitError}
					onClose={() => setSubmitError(null)}
				/>
			)}

			{invoice?.collectViaStripe && (
				<Alert
					type="info"
					showIcon
					style={{ marginBottom: 12 }}
					message="Stripe-collected invoice"
					description="It can only be settled in full out of band. Record the full balance, or void and re-issue it as a local invoice."
				/>
			)}

			<Text type="secondary" style={{ fontSize: 12 }}>
				Balance due {formatCents(balanceDueCents)}
			</Text>

			<Form
				form={form}
				layout="vertical"
				style={{ marginTop: 12 }}
				onFinish={(values) => mutation.mutate(values)}
			>
				<Row gutter={[16, 0]}>
					<Col xs={24} md={12}>
						<Form.Item
							label="Amount"
							name="amountDollars"
							rules={[
								{ required: true, message: "Enter the amount received" },
								{
									validator: (_, value) =>
										Number(value) > 0
											? Promise.resolve()
											: Promise.reject(new Error("Amount must be more than $0.00")),
								},
							]}
						>
							{/*
							  ⚠️ The `max` is a KEYBOARD guard, not the rule. The server re-derives
							  the balance under a row lock and answers 400 either way — which is
							  the check that counts, because this component's `balanceDueCents` is
							  a snapshot that another operator may already have invalidated.

							  It is capped for a STRIPE-collected invoice specifically because
							  that one has no legal part payment at all: Stripe's
							  `paid_out_of_band` marks the whole invoice paid and has no partial
							  equivalent, so any amount below the balance is refused. Leaving a
							  local invoice uncapped keeps part payments a single keystroke.
							*/}
							<InputNumber
								min={0}
								max={
									invoice?.collectViaStripe
										? centsToDollarsNumber(balanceDueCents)
										: undefined
								}
								step={0.01}
								prefix="$"
								style={{ width: "100%" }}
								data-testid="record-payment-amount"
							/>
						</Form.Item>
					</Col>
					<Col xs={24} md={12}>
						<Form.Item
							label="Method"
							name="method"
							rules={[{ required: true, message: "Select how the money arrived" }]}
						>
							<Select options={METHOD_OPTIONS} data-testid="record-payment-method" />
						</Form.Item>
					</Col>
				</Row>

				<Form.Item
					label="Reference"
					name="reference"
					extra="Cheque number, EFT reference, receipt book number."
				>
					<Input maxLength={255} placeholder="Optional" />
				</Form.Item>

				<Form.Item
					label="Date received"
					name="receivedAt"
					extra="The date the money arrived, not the date it was typed in."
				>
					<DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} />
				</Form.Item>
			</Form>
		</Modal>
	);
};

export default RecordPaymentModal;
